import { useEffect } from "react";
import { createForm } from "../../utils/formUtils";
import useForm from "../../hooks/common/useForm";
import useSet from "../../hooks/common/useSet";
import {
  modalFormHooks,
  modalFormSubmitHooks,
} from "../../hooks/handlers/useModalFormHandlers";
import { beautify, getValidators } from "../../utils/helpers";
import styled from "styled-components";
import { Button } from "antd";
import { Create } from "@mui/icons-material";
import useGet from "../../hooks/common/useGet";
import { sourceApis } from "../../hooks/handlers/useApiHandler";
import Loader from "../common/Loader";
import { TFormLayoutHandler } from "../../types/common";
import { useToasts } from "../../hooks/common/useToasts";

const ModalFormHandler = ({
  id,
  source_id: source,
  customTitle,
  form_params,
  additionalOptions,
  dismiss,
  reloadData,
}: TFormLayoutHandler & { additionalOptions?: any; dismiss?: () => void }) => {
  const {
    form,
    editedForm,
    updateFormValues,
    updateFormValue,
    onChange,
    validateForm,
    resetForm,
  } = useForm();

  const {
    error,
    setError,
    uploadData,
    submitting,
    success,
    data: submitResponse,
  } = useSet();

  const { data, loadData, loading } = useGet();

  // destructure form hooks using source id
  const {
    sections,
    title,
    redirect,
    options,
    defaultValues,
    callback,
    onSubmit: handleSubmit,
    params,
    retrieveRoute,
  } = source && modalFormHooks[source]
    ? modalFormHooks[source]({
        form,
        updateFormValue,
        onChange,
        updateFormValues,
        setError,
        id,
        form_params,
      })
    : [];

  // set form default values
  useEffect(() => {
    const mergedDefaults = { ...additionalOptions, ...defaultValues };
    if (mergedDefaults && Object.keys(mergedDefaults).length > 0) {
      updateFormValues(mergedDefaults);
    }
  }, []);

  // handle notification
  useToasts({
    success,
    error,
    redirect,
    callback,
    id,
    submitResponse,
    source,
    submitting,
  });

  // handle secondary notification
  // useToasts({
  //   success: draftSuccess,
  //   error: draftError,
  //   redirect: draftRedirect,
  //   id,
  //   submitResponse: draftSubmitResponse,
  //   submitting: draftSubmitting,
  //   successMessage: "Draft Saved Successfully",
  // });
  // handle delete draft notification
  // useToasts({
  //   success: deleteDraftSuccess,
  //   error: deleteDraftError,
  //   successMessage: "Draft Deleted Successfully",
  // });

  // get record data from api
  useEffect(() => {
    if (id) {
      let getRoute = "";
      if (source && sourceApis[source]) {
        getRoute = sourceApis[source];
        loadData({ id, api: getRoute, params });
      }
      if (retrieveRoute) {
        getRoute = retrieveRoute;
        loadData({ api: getRoute, params });
      }
    }
  }, [id]);

  useEffect(() => {
    // Handle successful submission
    if (success) {
      // if (draft_id) {
      //   deleteDraft({ api: draftsApi, id: draft_id, method: "DELETE" });
      // }
      // Reset form after successful submission if it's a new record
      if (!id) {
        resetForm();
      }
      // Close modal on successful submission
      if (dismiss) {
        // Delay dismiss to allow toast notification to show
        setTimeout(() => dismiss(), 500);
      }
    }
  }, [success, dismiss]);

  // update form data
  useEffect(() => {
    if (data) {
      updateFormValues(data);
    }
  }, [data]);

  // submit data
  const onSubmit = (payload: any) => {
    if (source) {
      // construct upload data
      delete payload.id;
      delete payload.created_by;
      delete payload.application_number;
      delete payload.created_at;
      delete payload.updated_at;

      let upload_data: any;

      // submit method from a form hook
      if (handleSubmit) {
        upload_data = handleSubmit(payload, id);
      } else if (modalFormSubmitHooks[source]) {
        // submit method from form handler
        upload_data = modalFormSubmitHooks[source](
          payload,
          id,
          form_params,
          reloadData,
        );
      }

      // handle submission
      if (upload_data) return uploadData(upload_data);
    }
  };

  const submitForm = (e: any) => {
    e.preventDefault();
    const payload = id ? editedForm : form;
    setError({});
    if (validateForm(getValidators(sections), setError)) onSubmit(payload);
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={submitForm}>
      <Wrapper className="flexColumn gap20">
        <TitleWrapper className="flexNullCenter gap10 x100 whiteBg radius10">
          <Create fontSize="small" color="primary" />
          <p
            className="capitalize"
            style={{
              fontWeight: "bold",
              marginBottom: 0,
            }}
          >
            {customTitle || title || beautify(source)} Form
          </p>
        </TitleWrapper>

        {/* Form Creation */}
        {createForm(
          sections,
          form,
          updateFormValues,
          updateFormValue,
          onChange,
          error,
          options,
          setError,
        )}
        {/* Submit Buttons */}
        <ButtonWrapper>
          <Button
            onClick={dismiss}
            disabled={submitting}
            style={{ width: 110, color: "#ff4d4f", borderColor: "#ff4d4f" }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={submitting}
            loading={submitting}
            style={{ width: 110 }}
          >
            {id ? "Update" : "Submit"}
          </Button>
        </ButtonWrapper>
      </Wrapper>
    </form>
  );
};

export default ModalFormHandler;

const Wrapper = styled.div`
  padding: 0 20px 20px 20px;
  @media (max-width: 960px) {
  }
`;

const TitleWrapper = styled.div`
  padding: 10px;
  @media (max-width: 960px) {
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
